// src/components/POS/cart/CartSidebar.js
import React, { useState, useMemo, useCallback } from "react";
import { useCart } from "../../contexts/cartContext";
import { usePOSProductsContext } from "../../contexts/POSProductContext";
import { ShoppingCart } from "lucide-react";
import { useTheme } from "../../../CommonComponents/ThemeProvider";
import CartItemRow from "./CartItemRow";
import EditCartItemModal from "../modals/EditCartItemModal";
import PaymentPanel from "../payment/PaymentPanel";
import CashPaymentPanel from "../payment/CashPaymentPanel";
import PaymentSummary from "../payment/PaymentSummary";
import OrderSummaryModal from "../payment/OrderSummaryModal";
import OrderSuccessModal from "../payment/OrderSuccessModal";
import OrderFailedModal from "../payment/OrderFailedModal";
import ConfirmationModal from "../../../CommonComponents/ConfirmationModal";
import EmptyState from "../../../CommonComponents/EmptyState";
import { formatPrice } from "../../utils/FormatPrice";
import { createOrder } from "../../../../services/orderApi";
import { authStorage } from "../../../../utils/authStorage";

export default function CartSidebar() {
    const [paymentMethod, setPaymentMethod] = useState("cash");
    const { cart, removeFromCart, clearCart, cartCountHighlight } = useCart();
    const { currentTheme } = useTheme();
    const { updateStockAfterOrder } = usePOSProductsContext();

    const [editItem, setEditItem] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);
    const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(false);
    const [isOrderSuccessOpen, setIsOrderSuccessOpen] = useState(false);
    const [isPaymentErrorOpen, setIsPaymentErrorOpen] = useState(false);
    const [paymentErrorMessage, setPaymentErrorMessage] = useState("Failed to create order. Please try again.");
    const [pendingPayment, setPendingPayment] = useState(null);
    const [lastConfirmedOrder, setLastConfirmedOrder] = useState(null);
    const [lastAddedItem, setLastAddedItem] = useState(null);

    const subtotal = useMemo(
        () => cart.reduce((sum, item) => sum + item.price * item.qty, 0),
        [cart]
    );
    const total = subtotal;

    const handlePlaceOrder = useCallback(
        ({ paymentMethod, amountPaid }) => {
            if (cart.length === 0) return;
            setPendingPayment({ cart, subtotal, total, paymentMethod, cashReceived: amountPaid });
            setIsOrderSummaryOpen(true);
        },
        [cart, subtotal, total]
    );

    const confirmOrder = useCallback(async () => {
        try {
            const user = authStorage.getUser() || JSON.parse(sessionStorage.getItem("user") || "null");
            if (!user?.id) throw new Error("User not logged in");

            const mappedCart = pendingPayment.cart.map(item => ({
                product_id: item.productId, // always use productId for consistency
                qty: item.qty,
                price: item.price,
                product_name: item.name,
            }));

            const orderData = {
                cart: mappedCart,
                payment_method: pendingPayment.paymentMethod,
            };

            const response = await createOrder(orderData);

            // Update stock in UI immediately
            updateStockAfterOrder(mappedCart);

            const fullOrder = {
                ...response,
                cart: pendingPayment.cart,
                subtotal: pendingPayment.subtotal,
                total: pendingPayment.total,
                payment_method: pendingPayment.paymentMethod,
                cashReceived: pendingPayment.cashReceived,
            };

            setLastConfirmedOrder(fullOrder);
            clearCart();
            setIsOrderSummaryOpen(false);
            setIsOrderSuccessOpen(true);
            setPendingPayment(null);
            setLastAddedItem(null);
        } catch (error) {
            console.error("Order API error:", error);
            setPaymentErrorMessage(error?.response?.data?.error || error?.response?.data?.message || "Failed to create order. Please try again.");
            setIsOrderSummaryOpen(false);
            setIsPaymentErrorOpen(true);
        }
    }, [pendingPayment, clearCart, updateStockAfterOrder]);

    // Handler to close the order success modal and reset lastConfirmedOrder
    const handleOrderSuccessClose = () => {
        setIsOrderSuccessOpen(false);
        setLastConfirmedOrder(null);
    };

    return (
        <>
            <aside
                className="w-full rounded-xl shadow-md bg-white flex flex-col overflow-hidden mb-4"
                style={{ height: 380, maxHeight: '90vh' }}
            >
                <div
                    className="px-4 py-2 flex-shrink-0 flex justify-between items-center border-b border-gray-200"
                >
                    <div>
                        <h2
                            className="text-[15px] font-semibold text-gray-700 tracking-normal"
                        >
                            {cart.length > 0
                                ? "Pending Order"
                                : lastConfirmedOrder
                                    ? `Order #${lastConfirmedOrder.order_number}`
                                    : "No Pending Order"}
                        </h2>
                        <p
                            className="text-xs mt-0.5 uppercase tracking-wide text-gray-500"
                        >
                            Ordered Items
                        </p>
                    </div>
                    <div className="relative">
                        <ShoppingCart size={24} className="text-black" />
                        {cart.length > 0 && (
                            <span
                                className={`absolute -top-2 -right-2 text-xs font-bold rounded-full px-2 py-0.5 transition-transform text-white ${cartCountHighlight ? "scale-125" : "scale-100"}`}
                                style={{ backgroundColor: currentTheme?.color || 'var(--color-primary)' }}
                            >
                                {cart.reduce((sum, i) => sum + i.qty, 0)}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1">
                        {cart.length > 0 ? (
                            cart.map(item => (
                                <CartItemRow
                                    key={item.lineId || item.id}
                                    item={item}
                                    formatPrice={formatPrice}
                                    onEdit={() => setEditItem(item)}
                                    onDelete={() => setDeleteItem(item)}
                                    highlightKey={lastAddedItem}
                                />
                            ))
                        ) : (
                            <EmptyState icon={ShoppingCart} message="Cart is empty" />
                        )}
                    </div>
                    <div
                        className="flex-shrink-0 px-4 py-3 rounded-b-xl border-t border-gray-200 bg-white"
                    >
                        <PaymentSummary subtotal={subtotal} total={total} />
                    </div>
                </div>

                {editItem && <EditCartItemModal item={editItem} onClose={() => setEditItem(null)} autoFocus={false} />}
                {deleteItem && (
                    <ConfirmationModal
                        isOpen={!!deleteItem}
                        onClose={() => setDeleteItem(null)}
                        onConfirm={() => {
                            removeFromCart(deleteItem.lineId || deleteItem.id, true);
                            setDeleteItem(null);
                        }}
                        title="Remove Item"
                        message={`Are you sure you want to remove "${deleteItem.name}" from the cart?`}
                        confirmText="Yes, Remove"
                        cancelText="Cancel"
                        type="delete"
                    />
                )}

                {isOrderSummaryOpen && pendingPayment && (
                    <OrderSummaryModal
                        isOpen={isOrderSummaryOpen}
                        onClose={() => setIsOrderSummaryOpen(false)}
                        order={{ ...pendingPayment }}
                        onConfirm={confirmOrder}
                    />
                )}


                {isOrderSuccessOpen && lastConfirmedOrder && (
                    <OrderSuccessModal
                        isOpen={isOrderSuccessOpen}
                        onClose={handleOrderSuccessClose}
                        orderNumber={lastConfirmedOrder.order_number}
                    />
                )}

                {isPaymentErrorOpen && (
                    <OrderFailedModal
                        isOpen={isPaymentErrorOpen}
                        onClose={() => setIsPaymentErrorOpen(false)}
                        message={paymentErrorMessage}
                    />
                )}
            </aside>

            <div className="">
                <PaymentPanel
                    total={total}
                    cart={cart}
                    onPay={handlePlaceOrder}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                />
                {paymentMethod === "cash" && (
                    <div className="mt-4">
                        <CashPaymentPanel total={total} cart={cart} onPay={handlePlaceOrder} />
                    </div>
                )}
            </div>
        </>
    );
}
